// src/ManualEntryPage.jsx - Manual Checkout, Custom Time, Direct Print
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue, update, get, set } from 'firebase/database';
import { database } from './firebase';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import { loadModels, matchFace } from './faceUtils';
import Navigation from "./Navigation";

export default function ManualEntryPage() {
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  
  // Manual checkout modal states
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedEmployeeForCheckout, setSelectedEmployeeForCheckout] = useState(null);
  const [customCheckoutTime, setCustomCheckoutTime] = useState('');
  const [customCheckoutMeridiem, setCustomCheckoutMeridiem] = useState('PM');
  
  // Face verification states
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [pendingMoney, setPendingMoney] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceAligned, setFaceAligned] = useState(false);
  const [recognizedEmployee, setRecognizedEmployee] = useState(null);
  const [recognitionConfidence, setRecognitionConfidence] = useState(0);
  const [autoVerificationDone, setAutoVerificationDone] = useState(false);
  
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const employeesCache = useRef([]);
  const cooldownRef = useRef(false);
  const detectionIntervalRef = useRef(null);

  // Get today's date in YYYY-MM-DD format
  function getTodayDate() {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().split("T")[0];
  }

  // Get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  // Parse time to minutes - CORRECTED VERSION
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === '-' || timeStr === null) return 0;
    try {
      let hours = 0, minutes = 0;
      let timeStrClean = timeStr.trim();
      
      const isPM = timeStrClean.toLowerCase().includes('pm');
      const isAM = timeStrClean.toLowerCase().includes('am');
      
      let timePart = timeStrClean;
      if (isPM || isAM) {
        timePart = timeStrClean.replace(/am|pm/i, '').trim();
      }
      
      const parts = timePart.split(':');
      if (parts.length >= 2) {
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      }
      
      if (isNaN(hours)) hours = 0;
      if (isNaN(minutes)) minutes = 0;
      
      if (isPM && hours < 12) {
        hours += 12;
      }
      if (isAM && hours === 12) {
        hours = 0;
      }
      
      hours = Math.min(23, Math.max(0, hours));
      minutes = Math.min(59, Math.max(0, minutes));
      
      return (hours * 60) + minutes;
    } catch (error) {
      console.error('Time parse error:', timeStr, error);
      return 0;
    }
  };

  // Format minutes to hours and minutes
  const formatHoursMinutes = (totalMinutes) => {
    if (totalMinutes <= 0) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Calculate total hours from check in and check out
  const calculateWorkedHours = (checkInTime, checkOutTime, isHalfDay = false) => {
    if (isHalfDay) return { minutes: 240, display: '4h 0m', decimal: 4.0 };
    if (!checkInTime || !checkOutTime || checkOutTime === '-') return { minutes: 0, display: '-', decimal: 0 };
    
    const checkInMinutes = parseTimeToMinutes(checkInTime);
    const checkOutMinutes = parseTimeToMinutes(checkOutTime);
    
    if (checkInMinutes === 0 || checkOutMinutes === 0) return { minutes: 0, display: '-', decimal: 0 };
    
    let totalMinutes = checkOutMinutes - checkInMinutes;
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }
    if (totalMinutes > 24 * 60) {
      totalMinutes = 24 * 60;
    }
    
    return {
      minutes: totalMinutes,
      display: formatHoursMinutes(totalMinutes),
      decimal: (totalMinutes / 60).toFixed(2)
    };
  };

  // Check if date is past (before today)
  const isPastDate = (dateStr) => {
    return dateStr < getTodayDate();
  };

  // Check if date is future (after today)
  const isFutureDate = (dateStr) => {
    return dateStr > getTodayDate();
  };

  useEffect(() => {
    loadEmployees();
    initFaceModels();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      employeesCache.current = employees;
      loadAttendanceRecords();
    }
  }, [selectedDate, employees]);

  const initFaceModels = async () => {
    try {
      await loadModels();
      console.log("Face models loaded for verification");
    } catch (err) {
      console.error("Error loading face models:", err);
    }
  };

  const loadEmployees = () => {
    const employeesRef = ref(database, 'employees');
    onValue(employeesRef, (snapshot) => {
      if (snapshot.exists()) {
        const employeesData = [];
        snapshot.forEach((child) => {
          employeesData.push({
            id: child.key,
            ...child.val()
          });
        });
        setEmployees(employeesData);
      }
    });
  };

  const loadAttendanceRecords = async () => {
    setLoading(true);
    const attendanceRef = ref(database, `attendance/${selectedDate}`);
    const snapshot = await get(attendanceRef);
    const records = snapshot.val() || {};
    
    const recordsWithEmployees = employees.map(employee => {
      const record = records[employee.id];
      const dateStr = selectedDate;
      
      let status = 'not-marked';
      let markedBy = null;
      let isManualMark = false;
      
      if (record) {
        if (record.markedBy === 'face-recognition' || record.markedBy === 'face-recognition-halfday') {
          markedBy = 'auto';
        } else if (record.markedBy === 'manual-entry' || record.attendanceMarkedManually) {
          markedBy = 'manual';
          isManualMark = true;
        } else if (record.checkInTime) {
          markedBy = 'auto';
        } else {
          markedBy = 'manual';
        }
        
        if (record.halfDay) {
          status = 'half-day';
        } else if (record.checkInTime && record.checkOutTime && record.checkOutTime !== '-') {
          status = 'present';
        } else if (record.checkInTime && (!record.checkOutTime || record.checkOutTime === '-')) {
          status = 'at-work';
        } else if (record.status === 'present') {
          status = 'present';
        } else if (record.status === 'absent') {
          status = 'absent';
        } else if (record.status === 'half-day') {
          status = 'half-day';
        } else {
          status = 'present';
        }
      } else if (isPastDate(dateStr) && !record) {
        status = 'absent';
        markedBy = 'auto';
      } else if (isFutureDate(dateStr) && !record) {
        status = 'not-marked';
      }
      
      const hours = calculateWorkedHours(record?.checkInTime, record?.checkOutTime, record?.halfDay);
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        signatureUrl: employee.signatureUrl || null,
        faceDescriptors: employee.faceDescriptors || (employee.descriptor ? [employee.descriptor] : []),
        status: status,
        checkInTime: record?.checkInTime || null,
        checkOutTime: record?.checkOutTime || null,
        totalHoursDisplay: hours.display,
        totalMinutes: hours.minutes,
        moneyTaken: record?.moneyTaken || 0,
        remark: record?.remark || '',
        markedBy: markedBy,
        verifiedBy: record?.verifiedBy || null,
        halfDay: record?.halfDay || false,
        hasRecord: !!record,
        isManualMark: isManualMark
      };
    });
    
    setAttendanceRecords(recordsWithEmployees);
    setLoading(false);
  };

  const updateAttendance = async (employeeId, updates) => {
    const attendanceRef = ref(database, `attendance/${selectedDate}/${employeeId}`);
    
    try {
      const snapshot = await get(attendanceRef);
      const existing = snapshot.val() || {};
      
      let recalculatedHours = null;
      if (updates.checkInTime !== undefined || updates.checkOutTime !== undefined || updates.halfDay !== undefined) {
        const newCheckIn = updates.checkInTime !== undefined ? updates.checkInTime : existing.checkInTime;
        const newCheckOut = updates.checkOutTime !== undefined ? updates.checkOutTime : existing.checkOutTime;
        const newHalfDay = updates.halfDay !== undefined ? updates.halfDay : existing.halfDay;
        
        if (newCheckIn && newCheckOut && newCheckOut !== '-') {
          recalculatedHours = calculateWorkedHours(newCheckIn, newCheckOut, newHalfDay);
          updates.totalHours = recalculatedHours.decimal;
          updates.totalMinutes = recalculatedHours.minutes;
          updates.totalTimeDisplay = recalculatedHours.display;
        } else if (newHalfDay) {
          recalculatedHours = calculateWorkedHours(newCheckIn, newCheckOut, true);
          updates.totalHours = recalculatedHours.decimal;
          updates.totalMinutes = recalculatedHours.minutes;
          updates.totalTimeDisplay = recalculatedHours.display;
        }
      }
      
      const updatedData = {
        ...existing,
        employeeId: employeeId,
        date: selectedDate,
        ...updates,
        updatedAt: Date.now()
      };
      
      await set(attendanceRef, updatedData);
      return true;
    } catch (error) {
      console.error('Update error:', error);
      return false;
    }
  };

  // Handle status change - updates immediately
  const handleStatusChange = async (employeeId, newStatus) => {
    let statusText = '';
    let halfDay = false;
    const currentTime = getCurrentTime();
    const employee = attendanceRecords.find(r => r.employeeId === employeeId);
    
    switch(newStatus) {
      case 'present':
        statusText = 'present';
        break;
      case 'at-work':
        statusText = 'at-work';
        break;
      case 'half-day':
        statusText = 'half-day';
        halfDay = true;
        break;
      case 'absent':
        statusText = 'absent';
        break;
      default:
        statusText = 'not-marked';
    }
    
    const updates = {
      status: statusText,
      halfDay: halfDay,
      markedBy: 'manual-entry',
      attendanceMarkedManually: true,
      updatedAt: Date.now()
    };
    
    if ((newStatus === 'present' || newStatus === 'at-work') && !employee?.checkInTime) {
      updates.checkInTime = currentTime;
    }
    
    if (newStatus === 'absent') {
      updates.checkInTime = null;
      updates.checkOutTime = null;
      updates.totalHours = null;
      updates.totalMinutes = null;
      updates.totalTimeDisplay = null;
    }
    
    if (newStatus === 'half-day') {
      updates.halfDay = true;
      if (!employee?.checkInTime) {
        updates.checkInTime = currentTime;
      }
      updates.checkOutTime = currentTime;
      const hours = calculateWorkedHours(updates.checkInTime || employee?.checkInTime, currentTime, true);
      updates.totalHours = hours.decimal;
      updates.totalMinutes = hours.minutes;
      updates.totalTimeDisplay = hours.display;
    }
    
    await updateAttendance(employeeId, updates);
    await loadAttendanceRecords();
    
    setMessage(`✅ Status updated to ${statusText.toUpperCase()}`);
    setMessageType('success');
    setTimeout(() => setMessage(''), 1500);
  };

  // Manual Checkout with custom time
  const openManualCheckout = (employee) => {
    setSelectedEmployeeForCheckout(employee);
    // Set default checkout time to current time
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    setCustomCheckoutTime(`${hours.toString().padStart(2, '0')}:${minutes}`);
    setCustomCheckoutMeridiem(ampm);
    setShowCheckoutModal(true);
  };

  const handleManualCheckout = async () => {
    if (!selectedEmployeeForCheckout) return;
    
    const checkoutTime = `${customCheckoutTime} ${customCheckoutMeridiem}`;
    const employee = selectedEmployeeForCheckout;
    
    // Check if employee has check-in time
    if (!employee.checkInTime) {
      setMessage(`⚠️ ${employee.employeeName} has no check-in time. Please mark as present first.`);
      setMessageType('warning');
      setShowCheckoutModal(false);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Calculate hours
    const hours = calculateWorkedHours(employee.checkInTime, checkoutTime, false);
    
    const updates = {
      checkOutTime: checkoutTime,
      status: 'present',
      totalHours: hours.decimal,
      totalMinutes: hours.minutes,
      totalTimeDisplay: hours.display,
      markedBy: 'manual-entry',
      attendanceMarkedManually: true,
      updatedAt: Date.now()
    };
    
    await updateAttendance(employee.employeeId, updates);
    await loadAttendanceRecords();
    
    setMessage(`✅ Checkout for ${employee.employeeName} at ${checkoutTime} | Worked: ${hours.display}`);
    setMessageType('success');
    setShowCheckoutModal(false);
    setSelectedEmployeeForCheckout(null);
    setTimeout(() => setMessage(''), 3000);
  };

  // Start face detection loop with auto verification
  const startFaceDetection = useCallback(() => {
    if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
    
    detectionIntervalRef.current = setInterval(async () => {
      const video = webcamRef.current?.video;
      if (!video || video.readyState !== 4 || cooldownRef.current || autoVerificationDone) return;
      
      try {
        const detection = await faceapi
          .detectSingleFace(
            video,
            new faceapi.TinyFaceDetectorOptions({
              inputSize: 224,
              scoreThreshold: 0.5,
            })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();
        
        if (detection && detection.detection) {
          setFaceDetected(true);
          
          const box = detection.detection.box;
          const cx = video.videoWidth / 2;
          const cy = video.videoHeight / 2;
          const fx = box.x + box.width / 2;
          const fy = box.y + box.height / 2;
          const aligned = Math.abs(cx - fx) < 60 && Math.abs(cy - fy) < 60;
          setFaceAligned(aligned);
          
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            const w = video.clientWidth;
            const h = video.clientHeight;
            canvas.width = w;
            canvas.height = h;
            const scaleX = w / video.videoWidth;
            const scaleY = h / video.videoHeight;
            
            ctx.clearRect(0, 0, w, h);
            ctx.strokeStyle = aligned ? '#00ff88' : '#ff3b3b';
            ctx.lineWidth = 3;
            ctx.strokeRect(
              box.x * scaleX,
              box.y * scaleY,
              box.width * scaleX,
              box.height * scaleY
            );
          }
          
          if (aligned && detection.descriptor && employeesCache.current.length > 0 && pendingEmployee && !autoVerificationDone) {
            const desc = Array.from(detection.descriptor);
            const result = matchFace(desc, employeesCache.current, 0.6);
            
            if (result && result.user) {
              setRecognizedEmployee(result.user);
              setRecognitionConfidence(Math.round(result.confidence * 100));
              
              if (result.user.id === pendingEmployee.employeeId && result.confidence > 0.65) {
                setVerificationStatus('success');
                setAutoVerificationDone(true);
                
                setTimeout(async () => {
                  const updates = {
                    moneyTaken: pendingMoney,
                    moneyTakenDate: Date.now(),
                    verifiedBy: 'face-verified',
                    updatedAt: Date.now()
                  };
                  
                  await updateAttendance(pendingEmployee.employeeId, updates);
                  await loadAttendanceRecords();
                  
                  setMessage(`✅ Verified! Money ₹${pendingMoney} added for ${pendingEmployee.employeeName}`);
                  setMessageType('success');
                  
                  setTimeout(() => {
                    setShowVerificationModal(false);
                    setPendingEmployee(null);
                    setPendingMoney(null);
                    setVerificationStatus(null);
                    setAutoVerificationDone(false);
                    setRecognizedEmployee(null);
                    setFaceDetected(false);
                    setFaceAligned(false);
                  }, 1500);
                }, 500);
              }
            } else {
              setRecognizedEmployee(null);
              setRecognitionConfidence(0);
            }
          }
        } else {
          setFaceDetected(false);
          setFaceAligned(false);
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      } catch (err) {
        console.error('Detection error:', err);
      }
    }, 200);
  }, [pendingEmployee, pendingMoney, autoVerificationDone]);

  useEffect(() => {
    if (showVerificationModal) {
      setAutoVerificationDone(false);
      setVerificationStatus(null);
      setRecognizedEmployee(null);
      setFaceDetected(false);
      setFaceAligned(false);
      startFaceDetection();
    }
    return () => {
      if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
    };
  }, [showVerificationModal, startFaceDetection]);

  // Handle money change - saves directly immediately
  const handleMoneyChange = async (employeeId, moneyAmount) => {
    const amount = parseFloat(moneyAmount) || 0;
    
    setAttendanceRecords(prev => prev.map(record => 
      record.employeeId === employeeId 
        ? { ...record, moneyTaken: amount, verifiedBy: amount === 0 ? null : record.verifiedBy }
        : record
    ));
    
    const updates = {
      moneyTaken: amount,
      moneyTakenDate: amount > 0 ? Date.now() : null,
      verifiedBy: amount === 0 ? null : (attendanceRecords.find(r => r.employeeId === employeeId)?.verifiedBy || null),
      updatedAt: Date.now()
    };
    
    await updateAttendance(employeeId, updates);
    
    if (amount > 0) {
      setMessage(`💰 Money updated: ₹${amount}`);
      setMessageType('info');
    } else {
      setMessage(`💰 Money removed`);
      setMessageType('info');
    }
    setTimeout(() => setMessage(''), 1500);
  };

  // Open verification modal when clicking Verify button
  const handleOpenVerification = (employee) => {
    if (!employee.moneyTaken || employee.moneyTaken <= 0) {
      setMessage(`⚠️ Please enter amount first before verification`);
      setMessageType('warning');
      setTimeout(() => setMessage(''), 2000);
      return;
    }
    
    setPendingEmployee(employee);
    setPendingMoney(employee.moneyTaken);
    setShowVerificationModal(true);
    setAutoVerificationDone(false);
  };

  const handleSkipVerification = async () => {
    if (pendingEmployee) {
      const updates = {
        moneyTaken: pendingMoney,
        moneyTakenDate: Date.now(),
        verifiedBy: 'skipped',
        updatedAt: Date.now()
      };
      
      await updateAttendance(pendingEmployee.employeeId, updates);
      await loadAttendanceRecords();
      
      setMessage(`💰 Money ₹${pendingMoney} added without verification for ${pendingEmployee.employeeName}`);
      setMessageType('warning');
      setShowVerificationModal(false);
      setPendingEmployee(null);
      setPendingMoney(null);
      setVerificationStatus(null);
      setAutoVerificationDone(false);
      setRecognizedEmployee(null);
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const handleRemarkChange = async (employeeId, remark) => {
    setAttendanceRecords(prev => prev.map(record => 
      record.employeeId === employeeId 
        ? { ...record, remark: remark }
        : record
    ));
    
    const updates = { 
      remark: remark,
      updatedAt: Date.now()
    };
    await updateAttendance(employeeId, updates);
  };

  // Direct Print
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    const totalMoney = attendanceRecords.reduce((sum, r) => sum + (r.moneyTaken || 0), 0);
    const presentCount = attendanceRecords.filter(r => r.status === 'present').length;
    const atWorkCount = attendanceRecords.filter(r => r.status === 'at-work').length;
    const halfDayCount = attendanceRecords.filter(r => r.halfDay).length;
    const absentCount = attendanceRecords.filter(r => r.status === 'absent').length;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Attendance Report - ${selectedDate}</title>
          <meta charset="UTF-8">
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            h1 { text-align: center; color: #333; font-size: 18pt; }
            .header { text-align: center; margin-bottom: 20px; }
            .date { color: #666; margin-bottom: 15px; }
            .summary { display: flex; justify-content: space-around; margin: 20px 0; flex-wrap: wrap; gap: 10px; }
            .summary-card { border: 1px solid #ddd; padding: 10px; border-radius: 8px; text-align: center; min-width: 100px; background: #f9f9f9; }
            .summary-number { font-size: 22px; font-weight: bold; color: #4CAF50; }
            .summary-label { font-size: 10px; color: #666; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px 6px; text-align: left; font-size: 9pt; }
            th { background-color: #4CAF50; color: white; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 8pt; color: #999; }
            @media print {
              body { margin: 0; padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ATTENDANCE REPORT</h1>
            <div class="date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          <div class="summary">
            <div class="summary-card"><div class="summary-number">${attendanceRecords.length}</div><div class="summary-label">Total Employees</div></div>
            <div class="summary-card"><div class="summary-number">${presentCount}</div><div class="summary-label">Present</div></div>
            <div class="summary-card"><div class="summary-number">${atWorkCount}</div><div class="summary-label">At Work</div></div>
            <div class="summary-card"><div class="summary-number">${halfDayCount}</div><div class="summary-label">Half Day</div></div>
            <div class="summary-card"><div class="summary-number">${absentCount}</div><div class="summary-label">Absent</div></div>
            <div class="summary-card"><div class="summary-number">₹${totalMoney}</div><div class="summary-label">Total Money</div></div>
          </div>
          <table>
            <thead>
              <tr><th>Employee</th><th>Status</th><th>IN</th><th>OUT</th><th>Hours</th><th>Money</th><th>Verification</th><th>Remark</th><th>Type</th></tr>
            </thead>
            <tbody>
              ${attendanceRecords.map(record => {
                let status = '';
                if (record.halfDay) status = 'HALF DAY';
                else if (record.status === 'present') status = 'PRESENT';
                else if (record.status === 'absent') status = 'ABSENT';
                else if (record.status === 'at-work') status = 'AT WORK';
                else status = 'NOT MARKED';
                
                const isVerified = record.verifiedBy === 'face-verified';
                const verificationText = isVerified ? '✓ Verified' : (record.verifiedBy === 'skipped' ? 'Skipped' : (record.moneyTaken > 0 ? 'Pending' : '-'));
                
                return `
                  <tr>
                    <td>${record.employeeName}</td>
                    <td>${status}</td>
                    <td>${record.checkInTime ? record.checkInTime.slice(0,5) : '-'}</td>
                    <td>${record.checkOutTime && record.checkOutTime !== '-' ? record.checkOutTime.slice(0,5) : '-'}</td>
                    <td>${record.totalHoursDisplay}</td>
                    <td>${record.moneyTaken ? '₹' + record.moneyTaken : '-'}${isVerified ? ' ✓' : ''}</td>
                    <td>${verificationText}</td>
                    <td>${record.remark || '-'}</td>
                    <td>${record.markedBy === 'auto' ? 'AUTO' : 'MANUAL'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="footer">Generated on: ${new Date().toLocaleString()} | Manual Entry System</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getStatusDisplay = (record) => {
    if (record.halfDay) return { text: '🌙 HALF DAY', color: '#ff9800' };
    if (record.status === 'present') return { text: '✅ PRESENT', color: '#4caf50' };
    if (record.status === 'absent') return { text: '❌ ABSENT', color: '#f44336' };
    if (record.status === 'at-work') return { text: '🟢 AT WORK', color: '#2196f3' };
    return { text: '⚪ NOT MARKED', color: '#9e9e9e' };
  };

  const getMarkedByDisplay = (record) => {
    if (record.markedBy === 'auto') return { text: '🤖 AUTO', color: '#1976d2' };
    if (record.markedBy === 'manual' || record.isManualMark) return { text: '✍️ MANUAL', color: '#f57c00' };
    if (record.hasRecord && !record.markedBy) return { text: '📝 SYSTEM', color: '#6c757d' };
    return { text: '-', color: '#999' };
  };

  const changeDate = (direction) => {
    const date = new Date(selectedDate);
    if (direction === 'prev') date.setDate(date.getDate() - 1);
    else if (direction === 'next') date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(getTodayDate());
  };

  const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const isToday = selectedDate === getTodayDate();
  const isPastSelected = isPastDate(selectedDate);
  const isFutureSelected = isFutureDate(selectedDate);
  const totalMoneyToday = attendanceRecords.reduce((sum, r) => sum + (r.moneyTaken || 0), 0);

  return (
    <div style={styles.container}>
      <Navigation role="hr" />
      <div style={styles.header}>
        <h1 style={styles.title}>📝 Manual Entry</h1>
        <p style={styles.subtitle}>Edit attendance | Manual Checkout | Direct Print</p>
      </div>

      {/* Date Navigation */}
      <div style={styles.dateNavSection}>
        <div style={styles.dateNavContainer}>
          <button onClick={() => changeDate('prev')} style={styles.navButton}>◀ Previous</button>
          <div style={styles.dateDisplay}>
            <span style={styles.dateText}>{formattedDate}</span>
            {!isToday && <button onClick={goToToday} style={styles.todayButton}>Today</button>}
          </div>
          <button onClick={() => changeDate('next')} style={styles.navButton}>Next ▶</button>
        </div>
        <div style={styles.summaryBar}>
          <span>📊 Total: {attendanceRecords.length} employees</span>
          <span>💰 Total Money: ₹{totalMoneyToday}</span>
          <button onClick={printReport} style={styles.printButton}>🖨️ PRINT</button>
        </div>
        {isPastSelected && (
          <div style={styles.dateWarning}>📅 Past date - Absent auto-marked for missing records</div>
        )}
        {isFutureSelected && (
          <div style={styles.dateWarning}>🔮 Future date - Shows as NOT MARKED</div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: messageType === 'success' ? '#d4edda' : 
                         messageType === 'error' ? '#f8d7da' : 
                         messageType === 'warning' ? '#fff3cd' : '#e3f2fd',
          color: messageType === 'success' ? '#155724' : 
                 messageType === 'error' ? '#721c24' : 
                 messageType === 'warning' ? '#856404' : '#1976d2'
        }}>
          {message}
        </div>
      )}

      {/* Main Table */}
      <div style={styles.tableSection}>
        <h2 style={styles.sectionTitle}>
          📋 Attendance Records
          {loading && <span style={styles.loadingBadge}>Loading...</span>}
        </h2>
        
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>⏱️ Hours</th>
                <th style={styles.th}>💰 Money</th>
                <th style={styles.th}>🔐 Verify</th>
                <th style={styles.th}>📝 Remark</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Type</th>
              </tr>
            </thead>
            <tbody>
              {attendanceRecords.map((record) => {
                const statusDisplay = getStatusDisplay(record);
                const markedByDisplay = getMarkedByDisplay(record);
                const isVerified = record.verifiedBy === 'face-verified';
                const hasMoney = record.moneyTaken > 0;
                const isAtWork = record.status === 'at-work';
                const hasCheckIn = record.checkInTime && !record.checkOutTime;
                
                return (
                  <tr key={record.employeeId} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div style={styles.employeeName}>{record.employeeName}</div>
                    </td>
                    
                    <td style={styles.td}>
                      <select
                        value={record.halfDay ? 'half-day' : (record.status === 'present' ? 'present' : (record.status === 'absent' ? 'absent' : (record.status === 'at-work' ? 'at-work' : 'not-marked')))}
                        onChange={(e) => handleStatusChange(record.employeeId, e.target.value)}
                        style={{
                          ...styles.statusSelect,
                          backgroundColor: statusDisplay.color + '20',
                          borderColor: statusDisplay.color,
                          color: statusDisplay.color
                        }}
                      >
                        <option value="present" style={{ color: '#4caf50' }}>✅ Present</option>
                        <option value="at-work" style={{ color: '#2196f3' }}>🟢 At Work</option>
                        <option value="half-day" style={{ color: '#ff9800' }}>🌙 Half Day</option>
                        <option value="absent" style={{ color: '#f44336' }}>❌ Absent</option>
                        <option value="not-marked" style={{ color: '#9e9e9e' }}>⚪ Not Marked</option>
                      </select>
                      {record.checkInTime && (
                        <div style={styles.timeInfo}>In: {record.checkInTime.slice(0,5)}</div>
                      )}
                      {record.checkOutTime && record.checkOutTime !== '-' && (
                        <div style={styles.timeInfo}>Out: {record.checkOutTime.slice(0,5)}</div>
                      )}
                      {isAtWork && (
                        <div style={styles.atWorkInfo}>🟢 Currently Working</div>
                      )}
                    </td>
                    
                    <td style={styles.td}>
                      <span style={styles.hoursDisplay}>{record.totalHoursDisplay}</span>
                    </td>
                    
                    <td style={styles.td}>
                      <input
                        type="number"
                        value={record.moneyTaken || ''}
                        onChange={(e) => handleMoneyChange(record.employeeId, e.target.value)}
                        style={styles.moneyInput}
                        placeholder="₹"
                        step="100"
                      />
                      {hasMoney && (
                        <div style={styles.moneyBadge}>
                          ₹{record.moneyTaken}
                          {isVerified && <span style={styles.verifiedCheck}> ✅</span>}
                        </div>
                      )}
                    </td>
                    
                    <td style={styles.td}>
                      {isVerified ? (
                        <span style={styles.verifiedBtn}>✅ Verified</span>
                      ) : (
                        <button
                          onClick={() => handleOpenVerification(record)}
                          style={styles.verifyButton}
                          disabled={!hasMoney}
                        >
                          🔐 Verify
                        </button>
                      )}
                    </td>
                    
                    <td style={styles.td}>
                      <input
                        type="text"
                        value={record.remark}
                        onChange={(e) => handleRemarkChange(record.employeeId, e.target.value)}
                        style={styles.remarkInput}
                        placeholder="Remark..."
                      />
                    </td>
                    
                    <td style={styles.td}>
                      {(hasCheckIn || isAtWork) && !record.checkOutTime && (
                        <button
                          onClick={() => openManualCheckout(record)}
                          style={styles.checkoutButton}
                        >
                          🚪 Manual Checkout
                        </button>
                      )}
                      {record.checkOutTime && record.checkOutTime !== '-' && (
                        <span style={styles.checkoutDone}>✓ Checked Out</span>
                      )}
                      {!hasCheckIn && !isAtWork && record.status !== 'present' && (
                        <span style={styles.noAction}>-</span>
                      )}
                    </td>
                    
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        backgroundColor: markedByDisplay.color + '20',
                        color: markedByDisplay.color
                      }}>
                        {markedByDisplay.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Checkout Modal */}
      {showCheckoutModal && selectedEmployeeForCheckout && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🚪 Manual Checkout</h3>
              <button onClick={() => setShowCheckoutModal(false)} style={styles.modalCloseBtn}>✕</button>
            </div>
            
            <p style={styles.modalText}>
              Employee: <strong>{selectedEmployeeForCheckout.employeeName}</strong>
            </p>
            <p style={styles.modalText}>
              Check In Time: <strong>{selectedEmployeeForCheckout.checkInTime}</strong>
            </p>
            
            <div style={styles.timeInputGroup}>
              <label style={styles.timeLabel}>Check Out Time:</label>
              <div style={styles.timeInputRow}>
                <input
                  type="text"
                  value={customCheckoutTime}
                  onChange={(e) => setCustomCheckoutTime(e.target.value)}
                  style={styles.timeInput}
                  placeholder="HH:MM"
                />
                <select
                  value={customCheckoutMeridiem}
                  onChange={(e) => setCustomCheckoutMeridiem(e.target.value)}
                  style={styles.meridiemSelect}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <p style={styles.timeHint}>Example: 06:30 PM or 09:00 AM</p>
            </div>
            
            <div style={styles.modalButtons}>
              <button onClick={handleManualCheckout} style={styles.confirmCheckoutBtn}>
                ✅ Confirm Checkout
              </button>
              <button onClick={() => setShowCheckoutModal(false)} style={styles.cancelCheckoutBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Face Verification Modal */}
      {showVerificationModal && pendingEmployee && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🔐 Face Verification (Optional)</h3>
              <button onClick={() => setShowVerificationModal(false)} style={styles.modalCloseBtn}>✕</button>
            </div>
            
            <p style={styles.modalText}>
              Employee: <strong>{pendingEmployee.employeeName}</strong>
            </p>
            <p style={styles.modalText}>
              Amount: <strong style={{ color: '#ff9800' }}>₹{pendingMoney}</strong>
            </p>
            <p style={styles.modalText}>
              <em style={{ color: '#2196f3' }}>Position your face in the green box for auto verification</em>
            </p>
            
            <div style={styles.cameraWrapper}>
              <Webcam
                ref={webcamRef}
                audio={false}
                videoConstraints={{ facingMode: "user", width: 400, height: 300 }}
                style={styles.camera}
                mirrored={true}
              />
              <canvas ref={canvasRef} style={styles.canvas} />
              
              {!faceDetected && (
                <div style={styles.alignOverlay}>
                  <p>😀 Place your face in frame</p>
                </div>
              )}
              {faceDetected && !faceAligned && (
                <div style={styles.alignOverlay}>
                  <p>🎯 Center your face in the GREEN box</p>
                </div>
              )}
              {faceDetected && faceAligned && recognizedEmployee && recognitionConfidence > 65 && (
                <div style={styles.recognizedOverlay}>
                  <p>✅ Recognized: {recognizedEmployee.name}</p>
                  <p>Confidence: {recognitionConfidence}%</p>
                </div>
              )}
              {faceDetected && faceAligned && (!recognizedEmployee || recognitionConfidence <= 65) && (
                <div style={styles.recognizedOverlay}>
                  <p>⚠️ Face not recognized</p>
                  <p>Try adjusting position</p>
                </div>
              )}
              
              {verificationStatus === 'success' && (
                <div style={styles.successOverlay}>
                  <div style={styles.checkmark}>✓</div>
                  <p>Verified! Adding money...</p>
                </div>
              )}
            </div>
            
            <div style={styles.modalButtons}>
              <button 
                onClick={handleSkipVerification} 
                style={styles.skipVerifyBtn}
              >
                ⚡ Skip Verification & Add Money
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '100%',
    margin: '0',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    textAlign: 'center',
    marginBottom: '15px'
  },
  title: {
    fontSize: '20px',
    color: '#333',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '11px',
    color: '#666'
  },
  dateNavSection: {
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '10px',
    marginBottom: '15px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
  },
  dateNavContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px'
  },
  navButton: {
    padding: '8px 16px',
    backgroundColor: '#ff1493',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  dateText: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#333'
  },
  todayButton: {
    padding: '5px 12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '11px'
  },
  summaryBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
    padding: '8px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 'bold',
    flexWrap: 'wrap',
    gap: '10px'
  },
  printButton: {
    padding: '6px 12px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  dateWarning: {
    marginTop: '8px',
    padding: '6px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    borderRadius: '6px',
    fontSize: '10px',
    textAlign: 'center'
  },
  message: {
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '15px',
    fontSize: '12px'
  },
  tableSection: {
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '15px',
    color: '#555',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '2px solid #4CAF50',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  loadingBadge: {
    fontSize: '10px',
    color: '#666',
    fontWeight: 'normal'
  },
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px'
  },
  tableHeader: {
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  th: {
    padding: '8px 4px',
    textAlign: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    color: 'white'
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0'
  },
  td: {
    padding: '6px 3px',
    textAlign: 'center',
    fontSize: '10px',
    verticalAlign: 'middle'
  },
  employeeName: {
    fontWeight: 'bold',
    fontSize: '11px',
    color: '#333'
  },
  statusSelect: {
    padding: '4px 6px',
    borderRadius: '15px',
    fontSize: '9px',
    fontWeight: 'bold',
    border: '1px solid',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '110px'
  },
  timeInfo: {
    fontSize: '8px',
    color: '#666',
    marginTop: '2px'
  },
  atWorkInfo: {
    fontSize: '8px',
    color: '#2196f3',
    marginTop: '2px',
    fontWeight: 'bold'
  },
  hoursDisplay: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#ff9800'
  },
  moneyInput: {
    width: '70px',
    padding: '4px',
    fontSize: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    textAlign: 'center'
  },
  moneyBadge: {
    fontSize: '8px',
    color: '#ff9800',
    fontWeight: 'bold',
    marginTop: '2px'
  },
  verifiedCheck: {
    color: '#4caf50'
  },
  verifyButton: {
    padding: '4px 10px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontSize: '9px',
    fontWeight: 'bold'
  },
  verifiedBtn: {
    padding: '4px 10px',
    backgroundColor: '#4caf50',
    color: 'white',
    borderRadius: '15px',
    fontSize: '9px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  remarkInput: {
    width: '90px',
    padding: '4px',
    fontSize: '9px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  checkoutButton: {
    padding: '4px 8px',
    backgroundColor: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontSize: '8px',
    fontWeight: 'bold'
  },
  checkoutDone: {
    fontSize: '8px',
    color: '#4caf50',
    fontWeight: 'bold'
  },
  noAction: {
    fontSize: '8px',
    color: '#999'
  },
  typeBadge: {
    padding: '2px 5px',
    borderRadius: '10px',
    fontSize: '8px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.95)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    width: '380px',
    maxWidth: '95%'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  modalTitle: {
    fontSize: '16px',
    margin: 0,
    color: '#333'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#999'
  },
  modalText: {
    fontSize: '13px',
    marginBottom: '10px',
    color: '#666'
  },
  timeInputGroup: {
    marginBottom: '15px'
  },
  timeLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#555',
    display: 'block',
    marginBottom: '5px'
  },
  timeInputRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  timeInput: {
    flex: 2,
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '6px'
  },
  meridiemSelect: {
    flex: 1,
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '6px'
  },
  timeHint: {
    fontSize: '10px',
    color: '#999',
    marginTop: '5px'
  },
  modalButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px'
  },
  confirmCheckoutBtn: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  cancelCheckoutBtn: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  skipVerifyBtn: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  cameraWrapper: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '15px'
  },
  camera: {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  alignOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    fontSize: '14px'
  },
  recognizedOverlay: {
    position: 'absolute',
    top: 10,
    left: 10,
    right: 10,
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '5px',
    borderRadius: '5px',
    color: '#00ff88',
    fontSize: '10px',
    textAlign: 'center'
  },
  successOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(76,175,80,0.9)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white'
  },
  checkmark: {
    fontSize: '40px',
    fontWeight: 'bold'
  }
};